import React from 'react';
import { Link } from 'react-router-dom';
import { Bike, ChevronRight, Play, Trophy, Tv } from 'lucide-react';
import { ProRace } from '@radevents/shared';
import { isRunning, daysUntilLabel } from '../utils/proRaceStatus';

/**
 * Eine Zeile Profi-Rennen, für den Rennkalender und „Radsport im TV“ (30.10.2026): Datumskachel,
 * Land und Kategorie, Name, darunter Countdown und Sender bzw. der Sieger. Auf der TV-Seite stehen
 * die Sender als Chips (`mitSendern`), weil sie dort die Auskunft sind.
 */
const raceTypeLabel: Record<string, string> = {
  ETAPPENRENNEN: 'Etappenrennen',
  EINTAGESRENNEN: 'Eintagesrennen',
};

// Maps a German country name to its ISO 3166-1 alpha-2 code (in case enrichment
// stores full names); an already-2-letter value is used as-is.
const COUNTRY_NAME_TO_CODE: Record<string, string> = {
  Frankreich: 'FR', Italien: 'IT', Spanien: 'ES', Belgien: 'BE', Niederlande: 'NL',
  Deutschland: 'DE', Schweiz: 'CH', Österreich: 'AT', Großbritannien: 'GB',
  'Vereinigtes Königreich': 'GB', Luxemburg: 'LU', Dänemark: 'DK', Polen: 'PL',
  Slowenien: 'SI', Kroatien: 'HR', 'Vereinigte Arabische Emirate': 'AE',
  Australien: 'AU', Kanada: 'CA', Norwegen: 'NO', Schweden: 'SE', USA: 'US',
};

/** ISO alpha-2 country code for the badge, or null if unknown. */
export function countryCode(country?: string): string | null {
  if (!country) return null;
  const c = country.trim();
  if (/^[A-Za-z]{2}$/.test(c)) return c.toUpperCase();
  return COUNTRY_NAME_TO_CODE[c] ?? null;
}

export const MONATE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
const MONATE_KURZ = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

/** Rennen nach dem Monat ihres Starts, in der Reihenfolge der Liste. */
export function nachMonat(races: ProRace[]): { key: string; label: string; races: ProRace[] }[] {
  const groups: { key: string; label: string; races: ProRace[] }[] = [];
  for (const race of races) {
    const [y, m] = race.startDate.split('-');
    const key = `${y}-${m}`;
    let g = groups[groups.length - 1];
    if (!g || g.key !== key) {
      g = { key, label: `${MONATE[Number(m) - 1]} ${y}`, races: [] };
      groups.push(g);
    }
    g.races.push(race);
  }
  return groups;
}

/** Sender aus „ARD, Eurosport 1, discovery+“ als Chips; Streams mit Play-Symbol in der Markenfarbe. */
export function BroadcasterChips({ info }: { info?: string }) {
  const items = (info ?? '').split(',').map(x => x.trim()).filter(Boolean);
  if (items.length === 0) return null;
  const stream = (name: string) => /mediathek|discovery\+|stream|joyn|dazn|sportdeutschland|\+$/i.test(name);
  return (
    <span className="rk-sender">
      {items.map((b, i) => (
        <span key={i} className={stream(b) ? 'rk-sender-chip rk-sender-chip--stream' : 'rk-sender-chip'}>
          {stream(b) ? <Play size={12} aria-hidden /> : <Tv size={12} aria-hidden />}{b}
        </span>
      ))}
    </span>
  );
}

export function RaceGroup({ label, races, live, ergebnis, mitSendern }: { label: string; races: ProRace[]; live?: boolean; ergebnis?: boolean; mitSendern?: boolean }) {
  return (
    <section className="rk-gruppe" aria-label={label}>
      <h2 className={live ? 'rk-monat rk-monat--live' : 'rk-monat'}>{live && <span className="rk-live-punkt" aria-hidden />}{label}</h2>
      <ol className="rk-liste">
        {races.map(race => <li key={race.id}><RaceRow race={race} ergebnis={ergebnis} mitSendern={mitSendern} /></li>)}
      </ol>
    </section>
  );
}

function RaceRow({ race, ergebnis, mitSendern }: { race: ProRace; ergebnis?: boolean; mitSendern?: boolean }) {
  const start = new Date(`${race.startDate}T00:00:00`);
  const end = new Date(`${race.endDate}T00:00:00`);
  const mehrtaegig = race.endDate !== race.startDate;
  const tag = mehrtaegig && start.getMonth() === end.getMonth()
    ? `${start.getDate()}–${end.getDate()}` : String(start.getDate());
  const monat = mehrtaegig && start.getMonth() !== end.getMonth()
    ? `${MONATE_KURZ[start.getMonth()]}–${MONATE_KURZ[end.getMonth()]}` : MONATE_KURZ[start.getMonth()];
  const code = countryCode(race.country);
  const running = isRunning(race.startDate, race.endDate);
  const countdown = daysUntilLabel(race.startDate, race.endDate);
  const art = raceTypeLabel[race.raceType] ?? race.raceType;
  const stages = race.stages?.length ? `${race.stages.length} Etappen` : null;

  return (
    <Link to={`/rennkalender/${race.slug}`} className={running ? 'rk-zeile rk-zeile--live' : 'rk-zeile'}>
      <span className="rk-kachel" aria-hidden>
        <span className="rk-kachel-monat">{monat}</span>
        <span className={tag.length > 2 ? 'rk-kachel-tag rk-kachel-tag--spanne' : 'rk-kachel-tag'}>{tag}</span>
      </span>
      <span className="rk-inhalt">
        <span className="rk-meta">
          {code && <span className="rk-land">{code}</span>}
          {[race.category?.replace(/^UCI\s+/, ''), stages ?? art].filter(Boolean).join(' · ')}
        </span>
        <span className="rk-name">{race.name}</span>
        {ergebnis ? (
          race.winner && <span className="rk-sieger"><Trophy size={14} aria-hidden />{race.winner}</span>
        ) : (
          <span className="rk-unten">
            {countdown && <span className={running ? 'rk-status rk-status--live' : 'rk-status'}>{countdown}</span>}
            {!mitSendern && race.broadcastInfo && <span className="rk-tv-info"><Tv size={13} aria-hidden />{race.broadcastInfo}</span>}
          </span>
        )}
        {mitSendern && !ergebnis && (race.broadcastInfo
          ? <BroadcasterChips info={race.broadcastInfo} />
          : <span className="rk-sender-offen">Übertragung noch offen</span>)}
      </span>
      {race.linkedEvents?.length > 0 && !ergebnis && (
        <span className="rk-mitfahren" title="Mit Jedermann-Rennen zum Mitfahren"><Bike size={15} aria-hidden /><span className="hide-mobile">Mitfahren</span></span>
      )}
      <ChevronRight size={18} className="rk-pfeil" aria-hidden />
    </Link>
  );
}
